import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, RefreshCcw, Database } from "lucide-react";

const ENTITIES = [
  { value: "orders", label: "Orders", description: "Every buyer order with totals, tax split, payment method and status." },
  { value: "vendors", label: "Vendors", description: "All vendor accounts joined with their KYC and agreement audit trail." },
  { value: "customers", label: "Customers", description: "Buyer accounts with acquisition source." },
  { value: "payouts", label: "Payouts", description: "Weekly vendor payout ledger with commission, deductions and UTR." },
  { value: "returns", label: "Returns", description: "Every return request (refund + replacement) filed by buyers." },
  { value: "refunds", label: "Refunds", description: "Return requests where the buyer asked for a refund only." },
];

export default function AdminExportsTab() {
  const [entity, setEntity] = useState("orders");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const activeMeta = ENTITIES.find((e) => e.value === entity);

  const buildParams = () => {
    const p = new URLSearchParams({ entity });
    if (start) p.append("start", `${start}T00:00:00+00:00`);
    if (end) p.append("end", `${end}T23:59:59+00:00`);
    return p.toString();
  };

  const refreshCount = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/exports/count?${buildParams()}`);
      setCount(data.count);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch count");
      setCount(null);
    } finally {
      setBusy(false);
    }
  };

  // Auto-refresh count whenever selection changes
  useEffect(() => {
    setCount(null);
    refreshCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, start, end]);

  const download = async () => {
    if (!count) return toast.error("Nothing to export in the selected range");
    setDownloading(true);
    try {
      const res = await api.get(`/admin/exports/download?${buildParams()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cityshor_${entity}_${start || "all"}_${end || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${count.toLocaleString("en-IN")} ${entity} row${count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-exports-tab">
      <div>
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-[#F48E2E]" />
          <div className="font-display text-2xl">Data Exports</div>
        </div>
        <p className="text-xs text-gray-500 mt-1">Download any dataset as a CSV. Use the date range to slice by <span className="font-mono-tab">created_at</span>. Leave both dates empty to export everything.</p>
      </div>

      <div className="p-5 bg-white border border-gray-200 rounded-2xl space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Dataset</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="exports-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITIES.map((e) => (
                  <SelectItem key={e.value} value={e.value} data-testid={`exports-entity-${e.value}`}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">From</Label>
            <Input data-testid="exports-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">To</Label>
            <Input data-testid="exports-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button
              data-testid="exports-refresh"
              onClick={refreshCount}
              disabled={busy}
              variant="outline"
              className="rounded-full w-full h-10"
            >
              <RefreshCcw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} />
              Refresh count
            </Button>
          </div>
        </div>

        <div className="text-xs text-gray-500">{activeMeta?.description}</div>

        <div className="rounded-2xl p-4 bg-[#FEF3E6] border border-[#F48E2E]/30 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-700">
            Records ready to export: {" "}
            <span data-testid="exports-count" className="font-display text-2xl text-[#111827] align-middle">
              {busy && count === null ? "…" : count === null ? "—" : count.toLocaleString("en-IN")}
            </span>
            {(start || end) && (
              <span className="text-xs text-gray-500 ml-2">
                ({start || "beginning"} → {end || "today"})
              </span>
            )}
          </div>
          <Button
            data-testid="exports-download"
            onClick={download}
            disabled={downloading || !count}
            className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white disabled:opacity-50"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? "Preparing…" : "Download CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
}
